/**
 * ModificationHistoryPanel (KAN-134)
 *
 * Right-hand detail panel for a selected history entry.
 * Uses the same mod-panel-* styling as ModificationDetailPanel
 * for visual consistency.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle2, XCircle, FileText, Loader2, Mail } from 'lucide-react';
import customDocumentService from '../../services/api/customDocumentService';
import GenerateDocumentModal from '../customDocuments/GenerateDocumentModal';
import { formatAmountDisplay, formatPriceDisplay } from '../../utils/amountFormatter';
import type { ModificationHistoryEntry } from '../../types/modification';
import type { DocumentTemplateSummary, DocumentGenerationResult } from '../../types/customDocuments';

interface Props {
  entry: ModificationHistoryEntry;
  clientId: string;
  onDocumentGenerated?: (results: DocumentGenerationResult[]) => void;
  onClose: () => void;
}

const ModificationHistoryPanel: React.FC<Props> = ({ entry, clientId, onDocumentGenerated, onClose }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.substring(0, 2) || 'es';

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [templates, setTemplates] = useState<DocumentTemplateSummary[]>([]);
  const [loadingDoc, setLoadingDoc] = useState(false);

  const formatDate = (value: any): string => {
    if (!value) return '-';
    const s = String(value).substring(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  };

  const formatNumber = (value: any): string => {
    if (value == null || value === '') return '-';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const isConfirmed = entry.modificationStatus === 'confirmed' || entry.modificationStatus === 'confirmed_inplace';
  const isInPlace = entry.modificationStatus === 'confirmed_inplace';
  const isRejected = entry.modificationStatus === 'rejected';

  // Use the new trade if it exists (Rollover/Settlement); fall back to original (Cancellation/Other)
  const generateTradeId = entry.newTradeId || entry.originalTradeId;

  const handleGenerateClick = async () => {
    setLoadingDoc(true);
    try {
      const tpls = await customDocumentService.listTemplates(clientId);
      setTemplates(tpls);
    } catch {
      setTemplates([]);
    } finally {
      setLoadingDoc(false);
      setShowGenerateModal(true);
    }
  };

  /* ── Unwind mailto builder ─────────────────────────────── */
  const handleUnwindMailto = () => {
    const mf = entry.modifiedFields || {};
    const tradeNum = entry.originalTradeNumber || '';
    const bankRef = (entry as any).CounterpartyTradeNumber || '';

    const labels: Record<string, {
      subject: string; greeting: string; intro: string;
      originalTradeHeader: string;
      entityLabel: string; counterpartyLabel: string;
      tradeLabel: string; bankRefLabel: string;
      directionLabel: string; amountLabel: string;
      tradeDateLabel: string; maturityLabel: string; priceLabel: string;
      unwindHeader: string; dateLabel: string;
      payProse: string; receiveProse: string;
      regards: string; dirBuy: string; dirSell: string;
    }> = {
      es: {
        subject: `Unwind Forward ${bankRef || tradeNum} - ${entry.OurParty || ''}`,
        greeting: 'Estimados,',
        intro: `Confirmamos el siguiente unwind de la operación ${bankRef ? `${bankRef} (Nuestra ref. ${tradeNum})` : `#${tradeNum}`}:`,
        originalTradeHeader: 'Datos de la operación original:',
        entityLabel: 'Entidad', counterpartyLabel: 'Contraparte',
        tradeLabel: 'Nuestra referencia', bankRefLabel: 'Referencia banco',
        directionLabel: 'Dirección', amountLabel: 'Monto',
        tradeDateLabel: 'Fecha de operación', maturityLabel: 'Vencimiento', priceLabel: 'Precio',
        unwindHeader: 'Liquidación del unwind:',
        dateLabel: 'Fecha de unwind',
        payProse: 'Pagamos {{amount}} — por favor cargar cuenta {{account}}.',
        receiveProse: 'Recibimos {{amount}} — por favor abonar cuenta {{account}}.',
        regards: 'Saludos,', dirBuy: 'Compra', dirSell: 'Venta',
      },
      en: {
        subject: `Forward Unwind ${bankRef || tradeNum} - ${entry.OurParty || ''}`,
        greeting: 'Dear Sir/Madam,',
        intro: `We confirm the following unwind of trade ${bankRef ? `${bankRef} (Our ref. ${tradeNum})` : `#${tradeNum}`}:`,
        originalTradeHeader: 'Original trade details:',
        entityLabel: 'Entity', counterpartyLabel: 'Counterparty',
        tradeLabel: 'Our reference', bankRefLabel: 'Bank reference',
        directionLabel: 'Direction', amountLabel: 'Amount',
        tradeDateLabel: 'Trade date', maturityLabel: 'Maturity', priceLabel: 'Price',
        unwindHeader: 'Unwind settlement:',
        dateLabel: 'Unwind date',
        payProse: 'We pay {{amount}} — please debit account {{account}}.',
        receiveProse: 'We receive {{amount}} — please credit account {{account}}.',
        regards: 'Kind regards,', dirBuy: 'Buy', dirSell: 'Sell',
      },
      pt: {
        subject: `Unwind Forward ${bankRef || tradeNum} - ${entry.OurParty || ''}`,
        greeting: 'Prezados,',
        intro: `Confirmamos o seguinte unwind da operação ${bankRef ? `${bankRef} (Nossa ref. ${tradeNum})` : `#${tradeNum}`}:`,
        originalTradeHeader: 'Dados da operação original:',
        entityLabel: 'Entidade', counterpartyLabel: 'Contraparte',
        tradeLabel: 'Nossa referência', bankRefLabel: 'Referência banco',
        directionLabel: 'Direção', amountLabel: 'Montante',
        tradeDateLabel: 'Data de operação', maturityLabel: 'Vencimento', priceLabel: 'Preço',
        unwindHeader: 'Liquidação do unwind:',
        dateLabel: 'Data do unwind',
        payProse: 'Pagamos {{amount}} — por favor debitar conta {{account}}.',
        receiveProse: 'Recebemos {{amount}} — por favor creditar conta {{account}}.',
        regards: 'Cumprimentos,', dirBuy: 'Compra', dirSell: 'Venda',
      },
    };

    const l = labels[locale] || labels['es'];

    // Format settlement amount
    const settlementCcy = mf.UnwindSettlementCurrency || '';
    const settlementDp = ['CLP', 'JPY', 'KRW'].includes(settlementCcy) ? 0 : 2;
    const settlementAmountStr = mf.UnwindSettlementAmount != null
      ? `${settlementCcy} ${formatAmountDisplay(Math.abs(Number(mf.UnwindSettlementAmount)), locale, settlementDp)}`
      : '';
    const uwDateStr = mf.UnwindDate ? formatDate(mf.UnwindDate) : '-';

    // Trade context
    const directionDisplay = entry.Direction === 'BUY' ? l.dirBuy : l.dirSell;
    const pair = `${entry.Currency1 || ''}/${(entry as any).Currency2 || ''}`;
    const tradeAmount = entry.QuantityCurrency1 != null
      ? `${formatNumber(entry.QuantityCurrency1)} ${entry.Currency1 || ''}`
      : '';

    const lines: string[] = [l.greeting, '', l.intro, ''];

    // ── Section 1: Original trade data ──
    lines.push(l.originalTradeHeader);
    if (entry.OurParty) lines.push(`  ${l.entityLabel}: ${entry.OurParty}`);
    if (entry.CounterpartyName) lines.push(`  ${l.counterpartyLabel}: ${entry.CounterpartyName}`);
    lines.push(`  ${l.tradeLabel}: ${tradeNum}`);
    if (bankRef) lines.push(`  ${l.bankRefLabel}: ${bankRef}`);
    if (entry.Direction) lines.push(`  ${l.directionLabel}: ${directionDisplay} ${pair}`);
    if (tradeAmount) lines.push(`  ${l.amountLabel}: ${tradeAmount}`);
    if ((entry as any).TradeDate) lines.push(`  ${l.tradeDateLabel}: ${formatDate((entry as any).TradeDate)}`);
    if (entry.MaturityDate) lines.push(`  ${l.maturityLabel}: ${formatDate(entry.MaturityDate)}`);
    if ((entry as any).Price) lines.push(`  ${l.priceLabel}: ${formatPriceDisplay(Number((entry as any).Price), locale, 4)}`);

    // ── Section 2: Unwind settlement (prose) ──
    lines.push('');
    lines.push(l.unwindHeader);
    lines.push(`  ${l.dateLabel}: ${uwDateStr}`);

    if (settlementAmountStr) {
      const accountName = mf.UnwindAccountName || '';
      if (mf.UnwindSettlementDirection === 'Payable') {
        const prose = l.payProse
          .replace('{{amount}}', settlementAmountStr)
          .replace('{{account}}', accountName || '—');
        lines.push(`  ${prose}`);
      } else {
        const prose = l.receiveProse
          .replace('{{amount}}', settlementAmountStr)
          .replace('{{account}}', accountName || '—');
        lines.push(`  ${prose}`);
      }
    }

    lines.push('', l.regards);

    const body = lines.join('\n');
    const mailto = `mailto:?subject=${encodeURIComponent(l.subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailto, '_blank');
  };

  const modTypeKey = entry.modificationType
    ? entry.modificationType === 'SettlementTypeChange'
      ? 'settlementTypeChange'
      : entry.modificationType.charAt(0).toLowerCase() + entry.modificationType.slice(1)
    : '';

  return (
    <>
    <div className="mod-panel">
      {/* Header */}
      <div className="mod-panel-header">
        <div className="mod-panel-header-left">
          <div className="mod-panel-header-row">
            <span className="mod-panel-trade-num">
              {t('modifications.detail.title', { tradeNumber: entry.originalTradeNumber })}
            </span>
            {modTypeKey && (
              <span className="mod-panel-mod-type">
                {t(`modifications.types.${modTypeKey}`)}
              </span>
            )}
          </div>
          {/* Status badge */}
          <div className="mod-panel-header-row">
            {isConfirmed && !isInPlace && (
              <span className="mod-panel-status-badge mod-panel-status-confirmed">
                <CheckCircle2 size={12} />
                {t('modifications.statusLabels.confirmed')}
              </span>
            )}
            {isInPlace && (
              <span className="mod-panel-status-badge mod-panel-status-confirmed">
                <CheckCircle2 size={12} />
                {entry.modificationType === 'Unwind'
                  ? t('modifications.statusLabels.unwound')
                  : t('modifications.statusLabels.confirmedInPlace')
                }
              </span>
            )}
            {isRejected && (
              <span className="mod-panel-status-badge mod-panel-status-rejected">
                <XCircle size={12} />
                {t('modifications.statusLabels.rejected')}
              </span>
            )}
          </div>
        </div>
        <div className="mod-panel-header-actions">
          {isConfirmed && entry.modificationType === 'Unwind' && (
            <button
              className="mod-panel-close"
              onClick={handleUnwindMailto}
              title={t('modifications.unwind.sendEmail', 'Enviar email de liquidación')}
            >
              <Mail size={16} />
            </button>
          )}
          {isConfirmed && entry.modificationType !== 'DraftTradeApproval' && (
            <button
              className="mod-panel-close"
              onClick={handleGenerateClick}
              disabled={loadingDoc}
              title={t('modifications.generateForm')}
            >
              {loadingDoc ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <FileText size={16} />}
            </button>
          )}
          <button className="mod-panel-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Trade summary */}
      <table className="mod-panel-table">
        <tbody>
          {entry.CounterpartyName && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.counterparty')}</td>
              <td className="mod-panel-current" colSpan={2}>{entry.CounterpartyName}</td>
            </tr>
          )}
          {entry.OurParty && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.entity')}</td>
              <td className="mod-panel-current" colSpan={2}>{entry.OurParty}</td>
            </tr>
          )}
          {(entry as any).ProductType && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.productType')}</td>
              <td className="mod-panel-current" colSpan={2}>{(entry as any).ProductType}</td>
            </tr>
          )}
          {entry.Direction && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.direction')}</td>
              <td className="mod-panel-current" colSpan={2}>{entry.Direction}</td>
            </tr>
          )}
          {(entry as any).TradeDate && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.tradeDate')}</td>
              <td className="mod-panel-current" colSpan={2}>{formatDate((entry as any).TradeDate)}</td>
            </tr>
          )}
          {entry.MaturityDate && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.maturityDate')}</td>
              <td className="mod-panel-current" colSpan={2}>{formatDate(entry.MaturityDate)}</td>
            </tr>
          )}
          {entry.QuantityCurrency1 != null && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.amount')}</td>
              <td className="mod-panel-current" colSpan={2}>
                {formatNumber(entry.QuantityCurrency1)} {entry.Currency1}
              </td>
            </tr>
          )}
          {(entry as any).Currency2 && (entry as any).QuantityCurrency2 != null && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.ccy2')}</td>
              <td className="mod-panel-current" colSpan={2}>
                {formatNumber((entry as any).QuantityCurrency2)} {(entry as any).Currency2}
              </td>
            </tr>
          )}
          {(entry as any).SpotPrice != null && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.spotPrice')}</td>
              <td className="mod-panel-current" colSpan={2}>{formatNumber((entry as any).SpotPrice)}</td>
            </tr>
          )}
          {(entry as any).ForwardPoints != null && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.fwdPts')}</td>
              <td className="mod-panel-current" colSpan={2}>{formatNumber((entry as any).ForwardPoints)}</td>
            </tr>
          )}
          {(entry as any).Price != null && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.price')}</td>
              <td className="mod-panel-current" colSpan={2}>{formatNumber((entry as any).Price)}</td>
            </tr>
          )}
          {(entry as any).SettlementType && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.settlementType')}</td>
              <td className="mod-panel-current" colSpan={2}>{(entry as any).SettlementType}</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Divider */}
      <div className="mod-panel-divider" />

      {/* Workflow info */}
      <table className="mod-panel-table">
        <tbody>
          {entry.workflowState && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.state')}</td>
              <td className="mod-panel-current" colSpan={2}>{entry.workflowState}</td>
            </tr>
          )}
          {entry.initiatedByName && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.initiatedBy')}</td>
              <td className="mod-panel-current" colSpan={2}>{entry.initiatedByName}</td>
            </tr>
          )}
          {entry.initiatedAt && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.requestDate')}</td>
              <td className="mod-panel-current" colSpan={2}>{formatDate(entry.initiatedAt)}</td>
            </tr>
          )}
          {entry.resolvedByName && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.resolvedBy')}</td>
              <td className="mod-panel-current" colSpan={2}>{entry.resolvedByName}</td>
            </tr>
          )}
          {entry.resolvedAt && (
            <tr>
              <td className="mod-panel-field-name">{t('modifications.columns.resolvedAt')}</td>
              <td className="mod-panel-current" colSpan={2}>{formatDate(entry.resolvedAt)}</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* New trade (Cancel+New confirmed) */}
      {isConfirmed && !isInPlace && entry.newTradeNumber && (
        <>
          <div className="mod-panel-divider" />
          <div className="mod-panel-result-row">
            <span className="mod-panel-field-name">{t('modifications.resultModal.newTrade')}</span>
            <span className="mod-panel-result-new-trade">#{entry.newTradeNumber}</span>
          </div>
        </>
      )}

      {/* Edit-in-place indicator (not shown for Unwind) */}
      {isInPlace && entry.modificationType !== 'Unwind' && (
        <>
          <div className="mod-panel-divider" />
          <div className="mod-panel-result-row">
            <span className="mod-panel-field-name">{t('modifications.resultModal.action')}</span>
            <span className="mod-panel-current" style={{ color: 'var(--text-secondary, #6b7280)', fontSize: '0.8125rem' }}>
              {t('modifications.resultModal.editedInPlaceNote')}
            </span>
          </div>
        </>
      )}

      {/* Changed fields — before/after table (confirmed) */}
      {isConfirmed && entry.changedFields && entry.changedFields.length > 0 && (() => {
        // Hide internal unwind fields from user-facing display
        const HIDDEN_UNWIND_FIELDS = new Set([
          'UnwindCalculationMode',
          'UnwindSettlementOverridden',
          'UnwindSettlementCurrency',
          'UnwindGrossResult',
          'UnwindDiscountConvention',
          'UnwindCargarAccountId',
          'UnwindAbonarAccountId',
        ]);
        const filtered = entry.changedFields.filter(f => !HIDDEN_UNWIND_FIELDS.has(f));
        if (filtered.length === 0) return null;
        const isUnwindEntry = entry.modificationType === 'Unwind';
        // Impose display order for Unwind fields
        const UNWIND_FIELD_ORDER = [
          'UnwindDate', 'UnwindSpotRate', 'UnwindForwardPoints', 'UnwindRate',
          'UnwindDiscountRate', 'UnwindSettlementAmount', 'UnwindSettlementDirection',
          'UnwindAccountName',
        ];
        const displayFields = isUnwindEntry
          ? UNWIND_FIELD_ORDER.filter(f => filtered.includes(f))
          : filtered;
        // Convert PascalCase field name to camelCase for translation lookup
        const fieldLabelKey = (field: string) => {
          const camel = field.charAt(0).toLowerCase() + field.slice(1);
          return t(`modifications.fields.${camel}`, field);
        };
        // Format a field value for display (Unwind-aware)
        const settlementCcy = entry.modifiedFields?.UnwindSettlementCurrency || '';
        const settlementDp = ['CLP', 'JPY', 'KRW'].includes(settlementCcy) ? 0 : 2;
        const formatFieldVal = (field: string, v: any): string => {
          if (v == null || v === '') return '-';
          const s = String(v);
          // Date
          const dm = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (dm) return `${dm[3]}/${dm[2]}/${dm[1]}`;
          // Unwind-specific formatting
          if (field === 'UnwindSettlementAmount') {
            return `${formatAmountDisplay(Number(v), locale, settlementDp)} ${settlementCcy}`;
          }
          if (field === 'UnwindDiscountRate') {
            return `${formatPriceDisplay(Number(v), locale, 4)}%`;
          }
          if (field === 'UnwindSettlementDirection') {
            return v === 'Receivable' ? t('modifications.unwind.weReceive') : t('modifications.unwind.wePay');
          }
          if (['UnwindSpotRate', 'UnwindForwardPoints', 'UnwindRate'].includes(field)) {
            return formatPriceDisplay(Number(v), locale, 6);
          }
          return s;
        };
        const sectionLabel = isUnwindEntry
          ? t('modifications.resultModal.unwindDetails')
          : t('modifications.resultModal.changedFields');
        return (
        <>
          <div className="mod-panel-divider" />
          <div className="mod-panel-section-label">{sectionLabel}</div>
          {entry.modifiedFields && entry.originalFieldValues ? (
            <table className="mod-panel-table mod-panel-changes-table">
              <thead>
                <tr>
                  <th>{t('modifications.detail.fieldLabel')}</th>
                  {!isUnwindEntry && <th>{t('modifications.detail.currentValues')}</th>}
                  <th>{isUnwindEntry ? t('modifications.resultModal.value') : t('modifications.detail.newValues')}</th>
                </tr>
              </thead>
              <tbody>
                {displayFields.map((field) => {
                  const rawFrom = entry.originalFieldValues![field];
                  const rawTo = entry.modifiedFields![field];
                  const override = entry.counterpartyOverrideFields?.[field];
                  return (
                    <tr key={field}>
                      <td className="mod-panel-field-name">
                        {fieldLabelKey(field)}
                      </td>
                      {!isUnwindEntry && (
                        <td className="mod-panel-current modifications-result-from">
                          {formatFieldVal(field, rawFrom)}
                        </td>
                      )}
                      <td className={isUnwindEntry ? 'mod-panel-current' : 'mod-panel-new modifications-result-to'}>
                        {formatFieldVal(field, rawTo)}
                        {override && (
                          <span
                            className="mod-panel-override-badge"
                            title={`${t('modifications.history.approvedValue')}: ${formatFieldVal(field, override.approvedValue)}`}
                          >
                            {t('modifications.history.counterpartyOverride')}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            // Fallback for older records that don't have modifiedFields stored
            <div className="mod-panel-changed-fields">
              {displayFields.map((field) => (
                <span key={field} className="mod-panel-field-tag">
                  {fieldLabelKey(field)}
                </span>
              ))}
            </div>
          )}
        </>
        );
      })()}

      {/* Rejection reason */}
      {isRejected && entry.rejectionReason && (
        <>
          <div className="mod-panel-divider" />
          <div className="mod-panel-section-label">{t('modifications.resultModal.reason')}</div>
          <div className="mod-panel-rejection-reason">{entry.rejectionReason}</div>
        </>
      )}
    </div>

    {showGenerateModal && (
      <GenerateDocumentModal
        clientId={clientId}
        selectedTradeIds={[generateTradeId]}
        templates={templates}
        onGenerated={async (results) => {
          setShowGenerateModal(false);
          for (const result of results) {
            if (result.status === 'SUCCESS' && result.recordId) {
              try {
                const { signedUrl, filename } = await customDocumentService.getDownloadUrl(clientId, result.recordId);
                const a = document.createElement('a');
                a.href = signedUrl;
                a.download = filename || '';
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              } catch {
                // fall through — toast will still show via onDocumentGenerated
              }
            }
          }
          onDocumentGenerated?.(results);
        }}
        onClose={() => setShowGenerateModal(false)}
      />
    )}
    </>
  );
};

export default ModificationHistoryPanel;
